import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'
import { usePageHeader } from '../../../providers/PageHeaderProvider'
import {
  Container,
  Header,
  Button,
  Cards,
  Box,
  Badge,
  Icon,
  SpaceBetween,
  Modal,
  StatusIndicator,
  Spinner,
  Form,
  FormField,
  Input,
  Textarea
} from '@cloudscape-design/components'
import { useCollection } from '@cloudscape-design/collection-hooks'

export const DashboardListPage: React.FC = () => {
  const navigate = useNavigate()
  const { sessionId } = useAmplifyAuth()
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newDashboardName, setNewDashboardName] = useState('')
  const [newDashboardDescription, setNewDashboardDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  
  // Fetch dashboards
  const dashboards = useQuery(
    sessionId ? api.components.dashboards.index.listDashboards : "skip",
    sessionId ? { sessionId } : "skip"
  )
  
  // Mutations
  const deleteDashboard = useMutation(api.components.dashboards.index.deleteDashboard)
  const createDashboard = useMutation(api.components.dashboards.index.createDashboard)

  // Set page header
  const { setHeaderConfig } = usePageHeader()
  
  // Memoize header actions to prevent unnecessary re-renders
  const headerActions = useMemo(() => [
    {
      id: 'create-dashboard',
      component: (
        <Button 
          variant="primary"
          iconName="add-plus"
          onClick={() => setShowCreateModal(true)}
        >
          Create Dashboard
        </Button>
      )
    }
  ], [navigate])

  // Set page header
  useEffect(() => {
    setHeaderConfig({
      title: 'Dashboards',
      description: dashboards ? `${dashboards.length} dashboard${dashboards.length !== 1 ? 's' : ''} configured` : 'Loading dashboards...',
      actions: headerActions
    })
  }, [dashboards, headerActions])

  // Collection hooks for filtering and searching
  const { items, filteredItemsCount, collectionProps } = useCollection(
    dashboards || [],
    {
      filtering: {
        filteringFunction: (item, filteringText) => {
          const searchableText = `${item.name} ${item.description || ''}`.toLowerCase()
          return searchableText.includes(filteringText.toLowerCase())
        },
        empty: (
          <Box textAlign="center" color="text-body-secondary" padding="xxl">
            <SpaceBetween size="m">
              <Icon name="status-info" size="big" />
              <Header variant="h2">No dashboards</Header>
              <div>Create your first dashboard to start visualizing your IoT data.</div>
              <Button variant="primary" iconName="add-plus" onClick={() => navigate('/dashboards/new')}>
                Create Dashboard
              </Button>
            </SpaceBetween>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="text-body-secondary" padding="xxl">
            <SpaceBetween size="m">
              <Icon name="search" size="big" />
              <Header variant="h3">No matching dashboards</Header>
              <div>Try adjusting your search criteria.</div>
            </SpaceBetween>
          </Box>
        ),
      },
    }
  )

  const handleDelete = async () => {
    if (!selectedDashboardId || !sessionId) return
    
    try {
      await deleteDashboard({ sessionId, dashboardId: selectedDashboardId })
      setShowDeleteModal(false)
      setSelectedDashboardId(null)
    } catch (error: any) {
      console.error('Failed to delete dashboard:', error)
    }
  }

  const handleCreate = async () => {
    if (!newDashboardName.trim() || !sessionId) return
    
    setIsCreating(true)
    try {
      const dashboardId = await createDashboard({
        sessionId,
        dashboard: {
          name: newDashboardName.trim(),
          description: newDashboardDescription.trim() || undefined,
          layout: [],
          widgets: [],
          default_time_range: '1h',
          refresh_interval: 30000,
          is_public: false
        }
      })
      
      setShowCreateModal(false)
      setNewDashboardName('')
      setNewDashboardDescription('')
      navigate(`/dashboards/${dashboardId}`)
    } catch (error: any) {
      console.error('Failed to create dashboard:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const getWidgetCountText = (count: number) => {
    return count === 0 ? 'No widgets' : 
           count === 1 ? '1 widget' : 
           `${count} widgets`
  }

  const getLastModifiedText = (timestamp?: number) => {
    if (!timestamp) return 'Never'
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    return date.toLocaleDateString()
  }

  // Show loading state
  if (dashboards === undefined) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <div>Loading dashboards...</div>
      </Box>
    )
  }

  return (
    <SpaceBetween size="m">
      {dashboards.length === 0 ? (
        <Container>
          <Box textAlign="center" color="text-body-secondary" padding="xxl">
            <SpaceBetween size="m">
              <Icon name="view-full" size="big" />
              <Header variant="h2">No dashboards yet</Header>
              <div>Create your first dashboard to start visualizing your IoT data.</div>
              <Button 
                variant="primary" 
                iconName="add-plus" 
                onClick={() => setShowCreateModal(true)}
              >
                Create Dashboard
              </Button>
            </SpaceBetween>
          </Box>
        </Container>
      ) : (
        <Cards
          cardsPerRow={[
            { cards: 1 },
            { minWidth: 500, cards: 2 },
            { minWidth: 800, cards: 3 }
          ]}
          items={items}
          loadingText="Loading dashboards..."
          empty={collectionProps.empty}
          noMatch={collectionProps.noMatch}
          cardDefinition={{
            header: item => (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="view-full" />
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>{item.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {item.is_public && <Badge color="green">Public</Badge>}
                  <StatusIndicator type="success">Active</StatusIndicator>
                </div>
              </div>
            ),
            sections: [
              {
                content: item => (
                  <Box>
                    {/* Description */}
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#5f6b7a',
                      marginBottom: '12px',
                      minHeight: '40px',
                      lineHeight: '1.4'
                    }}>
                      {item.description || 'No description provided'}
                    </div>

                    {/* Metadata */}
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '12px',
                      color: '#879596',
                      marginBottom: '16px'
                    }}>
                      <div>{getWidgetCountText(item.widgets?.length || 0)}</div>
                      <div>Modified {getLastModifiedText(item.updatedAt)}</div>
                    </div>

                    {/* Actions */}
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button 
                        onClick={() => navigate(`/dashboards/${item._id}`)}
                        variant="primary"
                      >
                        View
                      </Button>
                      <Button 
                        onClick={() => navigate(`/dashboards/${item._id}/edit`)}
                        iconName="edit"
                      >
                        Edit
                      </Button>
                      <Button 
                        onClick={() => {
                          setSelectedDashboardId(item._id)
                          setShowDeleteModal(true)
                        }}
                        iconName="remove"
                      >
                        Delete
                      </Button>
                    </SpaceBetween>
                  </Box>
                )
              }
            ]
          }}
        />
      )}

      {/* Create Dashboard Modal */}
      <Modal
        visible={showCreateModal}
        onDismiss={() => {
          setShowCreateModal(false)
          setNewDashboardName('')
          setNewDashboardDescription('')
        }}
        header="Create Dashboard"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                variant="link" 
                onClick={() => {
                  setShowCreateModal(false)
                  setNewDashboardName('')
                  setNewDashboardDescription('')
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleCreate}
                disabled={!newDashboardName.trim() || isCreating}
                loading={isCreating}
              >
                Create Dashboard
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Dashboard name" constraintText="Required">
            <Input
              value={newDashboardName}
              onChange={(event) => setNewDashboardName(event.detail.value)}
              placeholder="Enter dashboard name"
              disabled={isCreating}
            />
          </FormField>
          <FormField label="Description" constraintText="Optional">
            <Textarea
              value={newDashboardDescription}
              onChange={(event) => setNewDashboardDescription(event.detail.value)}
              placeholder="Describe what this dashboard will show"
              rows={3}
              disabled={isCreating}
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteModal}
        onDismiss={() => setShowDeleteModal(false)}
        header="Delete dashboard"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDelete}>
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <p>
          Are you sure you want to delete this dashboard? This action cannot be undone.
        </p>
      </Modal>
    </SpaceBetween>
  )
}